import React from 'react';
import { Menu, X, MapPin, Phone, Mail, Facebook, Instagram } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

interface NavbarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onOpenConsultation: () => void;
  activeFilterType?: string;
}

export default function Navbar({ activeTab, setActiveTab, onOpenConsultation, activeFilterType }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { content } = useSiteContent();

  const nav = content.navigation;
  const branding = content.branding;

  const linkColorClass = 'text-[#0F1E32] hover:text-[#C5A46E]';
  const activeColorClass = 'text-[#C5A46E]';

  return (
    <header className="absolute top-0 left-0 right-0 z-50 w-full flex flex-col font-sans">
      {/* Top Info Bar */}
      {nav.topBar.showTopBar && (
        <div className="bg-[#0F1E32] text-white py-2 px-3 md:py-2 md:px-8 border-b border-slate-800/40 text-[11px] md:text-xs">
          <div className="max-w-7xl mx-auto flex justify-between items-center gap-2">
            {/* Left Side: Contact Information */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-slate-200">
              {nav.topBar.addressText && (
                <a 
                  href={`https://maps.google.com/?q=${encodeURIComponent(nav.topBar.addressText)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hidden md:flex items-center gap-1.5 hover:text-[#C5A46E] transition-colors py-1 px-1.5 min-h-[32px]"
                >
                  <MapPin className="w-3.5 h-3.5 text-[#C5A46E]" />
                  <span>{nav.topBar.addressText}</span>
                </a>
              )}
              {nav.topBar.phone && (
                <a 
                  href={`tel:${nav.topBar.phone.replace(/[^\d+]/g, '')}`} 
                  className="flex items-center gap-1.5 hover:text-[#C5A46E] transition-colors py-1 px-2 min-h-[32px]"
                  aria-label={`Обадете се на ${nav.topBar.phone}`}
                >
                  <Phone className="w-3.5 h-3.5 text-[#C5A46E]" />
                  <span className="font-medium">{nav.topBar.phone}</span>
                </a>
              )}
              {nav.topBar.email && (
                <a 
                  href={`mailto:${nav.topBar.email}`} 
                  className="flex items-center gap-1.5 hover:text-[#C5A46E] transition-colors py-1 px-2 min-h-[32px]"
                  aria-label={`Изпратете имейл на ${nav.topBar.email}`}
                >
                  <Mail className="w-3.5 h-3.5 text-[#C5A46E]" />
                  <span className="hidden sm:inline">{nav.topBar.email}</span>
                  <span className="sm:hidden text-[10px]">Email</span>
                </a>
              )}
            </div>

            {/* Right Side: Social Media */}
            <div className="flex items-center gap-2 text-slate-300">
              {nav.topBar.facebookUrl && (
                <a href={nav.topBar.facebookUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[#C5A46E] transition-colors p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center" aria-label="Facebook страница на Варна Имотен Център">
                  <Facebook className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </a>
              )}
              {nav.topBar.instagramUrl && (
                <a href={nav.topBar.instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[#C5A46E] transition-colors p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center" aria-label="Instagram профил на Варна Имотен Център">
                  <Instagram className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Navbar - transparent overlay over the hero background as in reference */}
      <nav className="bg-transparent py-1 sm:py-2.5 px-4 md:px-8 transition-all">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Brand Logo - Scaled prominently without expanding header height */}
          {(() => {
            const logoSrc = (branding.logoUrl && branding.logoUrl !== '/imotencenterlogo.png') 
              ? branding.logoUrl 
              : '/imotencenterlogo-cropped.png';
            return (
              <a 
                href="/"
                id="brand-logo"
                onClick={(e) => { e.preventDefault(); setActiveTab('home'); }} 
                className="flex items-center group cursor-pointer focus:outline-none py-0.5 shrink-0"
                aria-label="Начална страница - Варна Имотен Център"
              >
                <img 
                  src={logoSrc} 
                  alt={branding.logoAlt || branding.siteTitle} 
                  className="h-11 xs:h-12 sm:h-14 md:h-16 lg:h-18 max-h-[80px] w-auto max-w-[calc(100vw-90px)] sm:max-w-none object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-sm"
                  referrerPolicy="no-referrer"
                />
              </a>
            );
          })()}

          {/* Desktop Navigation Links - Increased typography size and readability */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <a 
              href="/prodazhbi"
              id="nav-sales"
              onClick={(e) => { e.preventDefault(); setActiveTab('sales'); }} 
              className={`px-1 py-1 text-[17px] font-semibold transition-all duration-200 focus:outline-none cursor-pointer ${
                activeTab === 'sales' && activeFilterType !== 'all' ? 'text-[#C5A46E]' : 'text-[#0F1E32] hover:text-[#C5A46E]'
              }`}
            >
              {nav.menuLabels.sales}
            </a>
            <a 
              href="/naemi"
              id="nav-rentals"
              onClick={(e) => { e.preventDefault(); setActiveTab('rentals'); }} 
              className={`px-1 py-1 text-[17px] font-semibold transition-all duration-200 focus:outline-none cursor-pointer ${
                activeTab === 'rentals' ? 'text-[#C5A46E]' : 'text-[#0F1E32] hover:text-[#C5A46E]'
              }`}
            >
              {nav.menuLabels.rentals}
            </a>
            <a 
              href="/uslugi"
              id="nav-services"
              onClick={(e) => { e.preventDefault(); setActiveTab('services'); }} 
              className="px-1 py-1 text-[17px] font-semibold text-[#0F1E32] hover:text-[#C5A46E] transition-all duration-200 focus:outline-none cursor-pointer"
            >
              {nav.menuLabels.services}
            </a>
            <a 
              href="/za-nas"
              id="nav-about"
              onClick={(e) => { e.preventDefault(); setActiveTab('about'); }} 
              className={`px-1 py-1 text-[17px] font-semibold transition-all duration-200 focus:outline-none cursor-pointer ${
                activeTab === 'about' ? 'text-[#C5A46E]' : 'text-[#0F1E32] hover:text-[#C5A46E]'
              }`}
            >
              {nav.menuLabels.about}
            </a>
            <a 
              href="/ekip"
              id="nav-team"
              onClick={(e) => { e.preventDefault(); setActiveTab('team'); }} 
              className="px-1 py-1 text-[17px] font-semibold text-[#0F1E32] hover:text-[#C5A46E] transition-all duration-200 focus:outline-none cursor-pointer"
            >
              {nav.menuLabels.team}
            </a>
            <a 
              href="/kontakti"
              id="nav-contacts"
              onClick={(e) => { e.preventDefault(); setActiveTab('contacts'); }} 
              className="px-1 py-1 text-[17px] font-semibold text-[#0F1E32] hover:text-[#C5A46E] transition-all duration-200 focus:outline-none cursor-pointer"
            >
              {nav.menuLabels.contacts}
            </a>

            {/* Solid Golden CTA Button matching screenshot */}
            {nav.headerButton.show && (
              <button 
                id="btn-header-consult"
                onClick={onOpenConsultation}
                className="bg-[#C59B4B] hover:bg-[#b58c3f] text-white px-6 py-3 text-[15px] font-semibold tracking-normal transition-all focus:outline-none cursor-pointer ml-2 rounded-xl shadow-sm hover:shadow active:scale-95"
              >
                {nav.headerButton.text}
              </button>
            )}
          </div>

          {/* Mobile Right Controls */}
          <div className="flex lg:hidden items-center gap-3 pt-0 lg:pt-3">
            <button 
              id="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className="w-11 h-11 bg-white hover:bg-slate-50 text-[#0F1E32] rounded-xl shadow-md hover:shadow-lg border border-slate-200/90 flex items-center justify-center transition-all active:scale-95 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C5A46E]"
              aria-label={mobileMenuOpen ? "Затвори менюто" : "Отвори главното меню"}
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-[#0F1E32]" /> : <Menu className="w-6 h-6 text-[#0F1E32]" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute left-0 right-0 top-full bg-[#0F1E32]/98 backdrop-blur-md border-b border-slate-800 shadow-2xl px-4 py-5 flex flex-col gap-3.5 z-50">
            <a 
              href="/prodazhbi"
              id="mob-nav-sales"
              onClick={(e) => { e.preventDefault(); setActiveTab('sales'); setMobileMenuOpen(false); }} 
              className={`text-left py-2 px-3 rounded font-semibold text-sm transition-all ${
                activeTab === 'sales' && activeFilterType !== 'all' ? 'text-[#C5A46E] bg-white/5' : 'text-white/80 hover:text-white'
              }`}
            >
              {nav.menuLabels.sales}
            </a>
            <a 
              href="/naemi"
              id="mob-nav-rentals"
              onClick={(e) => { e.preventDefault(); setActiveTab('rentals'); setMobileMenuOpen(false); }} 
              className={`text-left py-2 px-3 rounded font-semibold text-sm transition-all ${
                activeTab === 'rentals' ? 'text-[#C5A46E] bg-white/5' : 'text-white/80 hover:text-white'
              }`}
            >
              {nav.menuLabels.rentals}
            </a>
            <a 
              href="/uslugi"
              id="mob-nav-services"
              onClick={(e) => { e.preventDefault(); setActiveTab('services'); setMobileMenuOpen(false); }} 
              className={`text-left py-2 px-3 rounded font-semibold text-sm transition-all ${
                activeTab === 'services' ? 'text-[#C5A46E] bg-white/5' : 'text-white/80 hover:text-white'
              }`}
            >
              {nav.menuLabels.services}
            </a>
            <a 
              href="/za-nas"
              id="mob-nav-about"
              onClick={(e) => { e.preventDefault(); setActiveTab('about'); setMobileMenuOpen(false); }} 
              className={`text-left py-2 px-3 rounded font-semibold text-sm transition-all ${
                activeTab === 'about' ? 'text-[#C5A46E] bg-white/5' : 'text-white/80 hover:text-white'
              }`}
            >
              {nav.menuLabels.about}
            </a>
            <a 
              href="/ekip"
              id="mob-nav-team"
              onClick={(e) => { e.preventDefault(); setActiveTab('team'); setMobileMenuOpen(false); }} 
              className={`text-left py-2 px-3 rounded font-semibold text-sm transition-all ${
                activeTab === 'team' ? 'text-[#C5A46E] bg-white/5' : 'text-white/80 hover:text-white'
              }`}
            >
              {nav.menuLabels.team}
            </a>
            <a 
              href="/kontakti"
              id="mob-nav-contacts"
              onClick={(e) => { e.preventDefault(); setActiveTab('contacts'); setMobileMenuOpen(false); }} 
              className={`text-left py-2 px-3 rounded font-semibold text-sm transition-all ${
                activeTab === 'contacts' ? 'text-[#C5A46E] bg-white/5' : 'text-white/80 hover:text-white'
              }`}
            >
              {nav.menuLabels.contacts}
            </a>
            {nav.headerButton.show && (
              <button 
                id="mob-btn-header-consult"
                onClick={() => { onOpenConsultation(); setMobileMenuOpen(false); }}
                className="mt-2 bg-[#C5A46E] text-white hover:bg-[#B1905E] py-2.5 px-4 rounded text-center font-bold text-sm uppercase tracking-wider transition-all"
              >
                {nav.headerButton.text}
              </button>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
